/**
 * Codex Design System — MUI Palette Builder
 *
 * Maps SemanticColors → MUI palette shape.
 * Kept separate so palette can be unit-tested independently of the full theme.
 */

import { alpha } from '@mui/material/styles';
import { palettes } from '../tokens/palettes';
import type { SemanticColors } from '../semantic/types';

export function buildPalette(c: SemanticColors) {
  const isLight = c.mode === 'light';

  return {
    mode: c.mode,

    primary: {
      main: c.primary,
      light: palettes.tyrian[300],
      dark: isLight ? palettes.tyrian[700] : palettes.tyrian[600],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: c.secondary,
      light: palettes.madder[300],
      dark: isLight ? palettes.madder[700] : palettes.madder[600],
      contrastText: '#FFFFFF',
    },
    error: {
      main: c.error,
      light: palettes.cinnabar[300],
      dark: isLight ? palettes.cinnabar[700] : palettes.cinnabar[600],
      contrastText: '#FFFFFF',
    },
    warning: {
      main: c.warning,
      light: palettes.saffron[300],
      dark: isLight ? palettes.saffron[700] : palettes.saffron[600],
      contrastText: '#212121',
    },
    info: {
      main: c.info,
      light: palettes.lapis[300],
      dark: isLight ? palettes.lapis[700] : palettes.lapis[600],
      contrastText: '#FFFFFF',
    },
    success: {
      main: palettes.laurel[700], // laurel[700] = #179345 exact match
      light: palettes.laurel[300],
      dark: isLight ? palettes.laurel[700] : palettes.laurel[600],
      contrastText: '#212121',
    },

    // MUI grey scale mapped to pumice palette
    grey: {
      50: palettes.pumice[50],
      100: palettes.pumice[100],
      200: palettes.pumice[200],
      300: palettes.pumice[300],
      400: palettes.pumice[400],
      500: palettes.pumice[500],
      600: palettes.pumice[600],
      700: palettes.pumice[700],
      800: palettes.pumice[800],
      900: palettes.pumice[900],
      A100: palettes.pumice[100],
      A200: palettes.pumice[200],
      A400: palettes.pumice[400],
      A700: palettes.pumice[700],
    },

    text: {
      primary: c.textPrimary,
      secondary: c.textSecondary,
      disabled: c.textDisabled,
    },
    background: {
      // default matches original modeColors.bg (#1F1F1F dark, #F7F7F8 light)
      // bgBody (#0F0F0F) is the page root — used via background.body below
      default: c.bgDefault,
      paper: isLight ? c.bgElevated : c.bgSurface,
      // Custom properties from original TypeBackground augmentation
      // Accessible via theme.palette.background.body / .dark / .light
      body: c.bgBody,
      dark: '#1F1F1F', // DARK_MODE_COLORS.bg — kept for backwards compat
      light: '#F7F7F8', // LIGHT_MODE_COLORS.bg — kept for backwards compat
    },
    divider: c.borderDefault,

    action: {
      active: c.textSecondary,
      hover: alpha(c.textPrimary, 0.04),
      hoverOpacity: 0.04,
      selected: alpha(c.primary, 0.08),
      selectedOpacity: 0.08,
      disabled: c.textDisabled,
      disabledBackground: alpha(c.textDisabled, 0.12),
      disabledOpacity: 0.38,
      focus: alpha(c.primary, 0.12),
      focusOpacity: 0.12,
    },
  } as const;
}
